import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { CoinTransactionView, CoinsHistoryPage, ReaderCoinsSummaryView } from '@/types/api';

/**
 * Reader-facing coin API — balance probe + history pagination. Coins are
 * never minted by the client directly; they arrive as a side-effect of
 * trusted system events (quiz completion today, reading challenges
 * tomorrow) and admin grants. The only `POST`s live on the admin
 * service below.
 */
export const coinsService = {
  /** Balance + recent activity preview for the header chip and balance hero. */
  getSummary(readerId: string | number): Promise<ReaderCoinsSummaryView> {
    return apiRequest<ReaderCoinsSummaryView>(apiRoutes.coins.summary(readerId));
  },

  /** Check if a reader has already earned coins from a specific quiz. */
  hasEarnedFromQuiz(readerId: string | number, quizId: string): Promise<{ earned: boolean }> {
    return apiRequest<{ earned: boolean }>(apiRoutes.coins.quizEarned(readerId, quizId));
  },

  /** Paginated full ledger, newest first. */
  getHistory(
    readerId: string | number,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<CoinsHistoryPage> {
    return apiRequest<CoinsHistoryPage>(apiRoutes.coins.history(readerId), {
      query: {
        limit: opts.limit,
        offset: opts.offset,
      },
    });
  },
};

/**
 * Admin coin writes + full-history lookup. Kept on a separate export so
 * the reader-facing service surface doesn't accidentally expose
 * mutation paths to non-admin callers via auto-complete.
 */
export const adminCoinsService = {
  /** Positive grant — promo credits, make-goods. */
  grant(
    readerId: string | number,
    payload: { amount: number; reason?: string },
  ): Promise<CoinTransactionView> {
    return apiRequest<CoinTransactionView>(apiRoutes.coins.adminGrant(readerId), {
      method: 'POST',
      body: payload,
    });
  },

  /** Signed adjustment — reason is required by the server DTO. */
  adjust(
    readerId: string | number,
    payload: { amount: number; reason: string },
  ): Promise<CoinTransactionView> {
    return apiRequest<CoinTransactionView>(apiRoutes.coins.adminAdjust(readerId), {
      method: 'POST',
      body: payload,
    });
  },

  getHistory(
    readerId: string | number,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<CoinsHistoryPage> {
    return apiRequest<CoinsHistoryPage>(apiRoutes.coins.adminHistory(readerId), {
      query: {
        limit: opts.limit,
        offset: opts.offset,
      },
    });
  },
};
